import { ParsedQs } from "qs";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { FilterContext } from "@/types/FilterContext";
import { StudentOrdering } from "@/types/StudentOrdering";
import type { FunnelPageState as FunnelPageStateType } from "@/pages/ZeemeeFunnel/types";
import { FunnelPageState } from "@/pages/ZeemeeFunnel/types";

export type FromUrlQuery<T> = (urlParams: ParsedQs) => T | undefined;
export type ToUrlQuery<T> = (data?: T) => UrlQueryObject;

export interface UrlSerializer<T> {
  fromUrlQuery: FromUrlQuery<T>;
  toUrlQuery: ToUrlQuery<T>;
}

export type UrlStateType = keyof typeof UrlSerializers;
export type UrlQueryObject = object;

/**** Serializers ****/
const FilterStateSerializer: UrlSerializer<FilterState> = {
  toUrlQuery: (data?: FilterState) => data?.toUrlQuery() || {},
  fromUrlQuery: (urlParams: ParsedQs): FilterState =>
    FilterState.fromUrlQuery(urlParams),
};

const FilterContextSerializer: UrlSerializer<FilterContext> = {
  toUrlQuery: (data?: FilterContext) => {
    return data?.toUrlQuery() || {};
  },
  fromUrlQuery: (urlParams: ParsedQs): FilterContext => {
    return FilterContext.fromUrlQuery(urlParams);
  },
};

const StudentOrderingSerializer: UrlSerializer<StudentOrdering> = {
  toUrlQuery: (data?: StudentOrdering) => {
    return data?.toUrlQuery() || {};
  },
  fromUrlQuery: (urlParams: ParsedQs): StudentOrdering | undefined => {
    return StudentOrdering.fromUrlQuery(urlParams) || undefined;
  },
};

const FunnelChartSerializer: UrlSerializer<FunnelPageStateType> = {
  toUrlQuery: (data?: FunnelPageStateType) =>
    data
      ? {
          funnelPage: {
            date: data.date,
            compareDate: data.compareDate,
            filterState: data.filterState?.toUrlQuery(),
            compareFilterState: data.compareFilterState?.toUrlQuery(),
          },
        }
      : {},
  fromUrlQuery: (urlParams?: ParsedQs): FunnelPageStateType | undefined => {
    const funnelPageParseResult = FunnelPageState.safeParse(
      urlParams?.["funnelPage"] || { date: undefined },
    );
    if (funnelPageParseResult.success) {
      return funnelPageParseResult.data;
    } else {
      console.error(
        `Could not parse funnel chart state from url query.`,
        funnelPageParseResult.error,
      );
    }
  },
};

export const UrlSerializers = {
  FILTER_STATE: FilterStateSerializer,
  FILTER_CONTEXT: FilterContextSerializer,
  STUDENT_ORDERING: StudentOrderingSerializer,
  FUNNEL_CHART: FunnelChartSerializer,
};
